import config from 'config'
import isUndefined from 'lodash/isUndefined'
import isNull from 'lodash/isNull'
import pickBy from 'lodash/pickBy'
import sortBy from 'lodash/sortBy'

// Requires vue-resource. See https://github.com/pagekit/vue-resource
// Refer to the documentation at https://marketplace-api.fanxchange.com

const handleError = response => {
    const errors = response?.body?.errors || response?.body?.detail || response?.body?.message || null
    return Promise.reject(errors)
}

const mapLocation = partnershipLocation => ({
    city: partnershipLocation.city,
    country_code: partnershipLocation.country_code,
    country_name: partnershipLocation.country,
    id: partnershipLocation.id,
    latitude: partnershipLocation.latitude,
    longitude: partnershipLocation.longitude,
    subdivision_code: partnershipLocation.state_code,
    subdivision: partnershipLocation.state
})

const mapAutocomplete = response => ({
    ...response.body,
    events: response.body.events.map(event => ({
        ...event,
        city: event.venue.city,
        region: event.venue.state_code,
        venue: event.venue.name
    }))
})

const mapEvent = event => ({
    ...event,
    city_name: event.venue.city || '',
    region: event.venue.state_code || '',
    images: event.images || {},
    venue: {
        id: event.venue.id,
        name: event.venue.name || '',
        address: event.venue.address || '',
        city_name: event.venue.city || '',
        subdivision: event.venue.state_code || '',
        state: event.venue.state || '',
        postal_code: event.venue.postal_code || '',
        country_code: event.venue.country_code || '',
        country: event.venue.country || '',
        region_id: event.venue.region_id,
        map: event.venue.map || '',
        map_url: event.venue.map_url || '',
        static_map: event.venue.static_map || ''
    },
    venueName: event.venue.name,
    venueCity: event.venue.city,
    venueRegionCode: event.venue.state_code
})

const mapMeta = response => ({
    current_page: response.page,
    last_page: response.number_of_pages,
    total: response.total
})

const mapLocalEvents = response => ({
    results: response.events,
    meta: mapMeta(response)
})

const mapPerformerOpponents = response => (
    response.body.items.map(item => ({
        id: item.id,
        key: item.name
    }))
)

const mapDetailEvent = response => ({
    ...response.body,
    venue: {
        ...response.body.venue,
        city_name: response.body.venue.city || ''
    }
})

const isSportsPerformer = performer => performer.segment_name.toLowerCase() === 'sports'

const mapEntityPerformer = ({ eventsResponse, performer, opponents, regions }) => {
    let aggs

    if (isSportsPerformer(performer)) {
        aggs = { performers: { opponents } }
    } else {
        aggs = {
            location: regions.map(x => ({
                key: x.city,
                id: x.id
            }))
        }
    }

    return {
        chronological: {
            aggs,
            meta: mapMeta(eventsResponse),
            results: eventsResponse.events
        }
    }
}

const mapEventsToChronological = response => ({
    chronological: {
        meta: mapMeta(response),
        results: response.events
    }
})

const mapSearch = response => ({
    chronological: {
        meta: mapMeta(response.body),
        results: response.body.items.map(mapEvent)
    },
    did_you_mean: { results: [] }
})

const mapEventsResponse = response => ({
    total: response.body.total,
    page: response.body.page,
    number_of_pages: response.body.number_of_pages,
    events: response.body.items.map(mapEvent)
})

const sortByCost = (a, b) => {
    return a.cost - b.cost
}

export default {
    install (Vue, options) {
        if (!Vue.http) {
            throw new Error('This module requires the vue-resource plugin to be registered on the Vue instance.')
        }

        // NOTE: where to put these helper methods??
        const buildPath = path => {
            return `${options.basePath}/v1/${path}`
        }
        const getCategory = value => value === 'arts & theatre' ? 'theatre' : value
        const getHomeOrAway = value => {
            if (['home', 'away'].includes(value)) {
                return value.toUpperCase()
            }

            return null
        }
        const removeUnneededValues = obj => pickBy(obj, v => !isUndefined(v) && !isNull(v))
        const sortByUtcDate = items => sortBy(items, item => item.utc_date)

        Vue.prototype.$marketplace = {
            autocomplete (query, config) {
                config = { ...config, params: { q: query } }
                return Vue.http
                    .get(buildPath('search-suggestions'), config)
                    .then(mapAutocomplete)
                    .catch(handleError)
            },
            getConfig () {
                return Promise.resolve({ rules: {} })
            },
            getEntityEvents (type, id, params) {
                if (type === 'performers') {
                    return this.getPerformer(id).then(performer => {
                        if (isSportsPerformer(performer)) {
                            return this.getEntityEventsSportsPerformer(performer, params)
                        }

                        return this.getEntityEventsNonSportsPerformer(performer, params)
                    })
                }

                if (type === 'venues') {
                    return this.getEntityEventsVenue(id, params)
                }
            },
            getEntityEventsVenue (id, params) {
                return this.getEvents({ ...params, venue_id: id })
                    .then(mapEventsToChronological)
                    .catch(handleError)
            },
            getEntityEventsSportsPerformer (performer, params) {
                return Promise.all([
                    this.getEvents({ ...params, performer_id: performer.id }),
                    this.getPerformerOpponents(performer.id)
                ]).then(response => {
                    const [eventsResponse, opponents] = response
                    return mapEntityPerformer({ eventsResponse, opponents, performer })
                })
            },
            getEntityEventsNonSportsPerformer (performer, params) {
                return Promise.all([
                    this.getEvents({ ...params, performer_id: performer.id }),
                    this.getRegions({ performer_id: performer.id })
                ]).then(response => {
                    const [eventsResponse, regions] = response
                    return mapEntityPerformer({ eventsResponse, regions, performer })
                })
            },
            getEvents (params = {}) {
                const eventParams = removeUnneededValues({
                    category: getCategory(params.category),
                    date_end: params.date_end,
                    date_start: params.date_start,
                    event_ids: params.event_ids,
                    home_or_away: getHomeOrAway(params.whereabouts),
                    opponent_id: params.opponent_id,
                    page: params.page,
                    per_page: params.per_page,
                    performer_id: params.performer_id,
                    region_id: params.location_id,
                    sort_by: params.sort_by,
                    sub_category_id: params.sub_genre_id,
                    venue_id: params.venue_id
                })

                return Vue.http.get(`${buildPath('events')}`, { params: eventParams })
                    .then(mapEventsResponse)
                    .catch(handleError)
            },
            getLocalEvents (params = {}) {
                return this.getEvents(params).then(mapLocalEvents).catch(handleError)
            },
            getLocalEventsCategorized (params = {}) {
                params.sort_by = 'RANK'

                return Promise.all([
                    this.getEvents({ ...params, category: 'sports' }),
                    this.getEvents({ ...params, category: 'music' }),
                    this.getEvents({ ...params, category: 'theatre' })
                ])
                    .then(responses => {
                        const [sportsResponse, musicResponse, theatreResponse] = responses
                        return {
                            results: {
                                'arts & theatre': { events: sortByUtcDate(theatreResponse.events) },
                                music: { events: sortByUtcDate(musicResponse.events) },
                                sports: { events: sortByUtcDate(sportsResponse.events) }
                            }
                        }
                    })
                    .catch(handleError)
            },

            getLocation (params, config = {}) {
                const request = { params, ...config }

                if (params && params.location_id) {
                    return this.getLocationById(params.location_id)
                }

                return Vue.http
                    .get(buildPath('regions/by_ip'), request)
                    .then(response => {
                        if (response.body.items.length) {
                            return mapLocation(response.body.items[0])
                        }

                        return null
                    })
                    .catch(handleError)
            },
            getLocationById (id) {
                return this.getRegions()
                    .then(regions => {
                        const region = regions.find(x => x.id === id)
                        return region ? mapLocation(region) : null
                    })
                    .catch(handleError)
            },
            getEvent (eventId) {
                return Vue.http
                    .get(buildPath(`events/${eventId}`))
                    .then(mapDetailEvent)
                    .catch(handleError)
            },
            getTickets (eventId, params, config) {
                const listingParams = removeUnneededValues({
                    event_id: eventId,
                    quantity: params.quantity,
                    min_price: params.min_price,
                    max_price: params.max_price,
                    stock_category: params.stock_category,
                    listing_ids: params.ticket_ids
                })
                config = { ...config, params: listingParams }

                return Vue.http
                    .get(buildPath('listings'), config)
                    .then(response => ({
                        meta: response.body.meta,
                        sections: response.body.sections,
                        tickets: response.body.listings
                    }))
                    .catch(handleError)
            },
            getTicketSummary (ticketId, params) {
                // This is first called on the checkout page load
                if (!isUndefined(params.eventId) && !isNull(params.eventId)) {
                    const eventId = params.eventId

                    return Vue.http
                        .get(`${options.basePath}/v1/events/${eventId}/listings/${ticketId}`, {
                            params
                        })
                        .then(response => {
                            // Backend should be sorting by cost, but good to make sure
                            response.body.delivery_options.sort(sortByCost)
                            return {
                                ticket: { ...response.body.listing, loyalty_pricing: response.body.loyalty_pricing, pricing: response.body.pricing },
                                face_value: (response.body.listing.face_value ? parseFloat(response.body.listing.face_value).toFixed(2) +' ea.' : 'Not provided by seller'),
                                show_face_value: response.body.listing.show_face_value,
                                event: mapEvent(response.body.event),
                                delivery_options: response.body.delivery_options,
                                cheapest_delivery_option: response.body.delivery_options[0]
                            }
                        })
                        .catch(handleError)
                } else {
                    return Promise.resolve({})
                }
            },
            getTicketPricing (ticketId, params, config = {}) {
                if (!isUndefined(params.eventId) && !isNull(params.eventId)) {
                    const eventId = params.eventId

                    params.shipping_country = params.shipping_country || params.billing_country

                    return Vue.http
                        .get(`${options.basePath}/v1/events/${eventId}/listings/${ticketId}`, {
                            params
                        })
                        .then(response => ({
                            loyalty_pricing: response.body.loyalty_pricing,
                            pricing: {
                                ...response.body.pricing,
                                original_price_per: response.body.pricing.price_per
                            },
                            delivery_options: response.body.delivery_options
                        }))
                        .catch(handleError)
                } else {
                    return Promise.resolve({})
                }
            },
            getTicketPricingPOST (ticketId, params, config = {}) {
                if (!isUndefined(params.eventId) && !isNull(params.eventId)) {
                    const eventId = params.eventId

                    params.shipping_country = params.shipping_country || params.billing_country

                    return Vue.http
                        .get(`${options.basePath}/v1/events/${eventId}/listings/${ticketId}`, {
                            params
                        })
                        .then(response => ({
                            loyalty_pricing: response.body.loyalty_pricing,
                            pricing: {
                                ...response.body.pricing,
                                original_price_per: response.body.pricing.price_per
                            },
                            delivery_options: response.body.delivery_options
                        }))
                        .catch(handleError)
                } else {
                    return Promise.resolve({})
                }
            },
            getPerformer (performerId) {
                return Vue.http
                    .get(buildPath(`performers/${performerId}`))
                    .then(response => response.body)
                    .catch(handleError)
            },
            getLocalPerformers (params) {
                const LEAGUES = {
                    nfl: 1,
                    mlb: 2,
                    nba: 3,
                    nhl: 4,
                    mls: 34
                }

                params.subgenre_id = LEAGUES[params.category]
                params.region_id = params.regionId

                return Vue.http
                    .get(buildPath('performers'), { params: params })
                    .then(response => {
                        return response.body.items
                    })
                    .catch(handleError)
            },
            getPerformerOpponents (performerId) {
                return Vue.http
                    .get(buildPath(`performers/${performerId}/opponents`))
                    .then(mapPerformerOpponents)
                    .catch(handleError)
            },
            getPromoAnnouncement (config = {}) {
                // Temporary: return a resolved promise to prevent JS errors
                return Promise.resolve({ announcement: null })
            },
            getBraintreeClientToken () {
                return Vue.http
                    .get(buildPath('payment/client-token'))
                    .then(response => response.body.token)
                    .catch(handleError)
            },
            getProfile (accountId) {
                return Vue.http
                    .get(buildPath('user'))
                    .then(response => ({
                        account: {
                            apple_pay_country: null,
                            apple_pay_region: null,
                            created_at: "",
                            date_of_birth: null,
                            email_address: response.body.email,
                            external_id: "",
                            first_name: response.body.first_name,
                            id: response.body.id,
                            last_name: response.body.last_name,
                            legacy_id: 0,
                            marketing_optin: false,
                            partner_id: config.wsuserId,
                            phone_number: response.body.phone,
                            updated_at: ""
                        },
                        loyalty: {
                            balance: response.body.loyalty_program.number_of_units,
                            currency_formatter: "0,0",
                            currency_label: response.body.loyalty_program.unit_display_name,
                            deferred_earn: false,
                            do_refresh_balance: true,
                            earn_per_dollar: response.body.loyalty_program.earn_per_dollar,
                            has_earn: response.body.loyalty_program.earn_per_dollar > 0,
                            has_redeem: response.body.loyalty_program.redeem_per_dollar > 0,
                            minimum_redemption_amount: null,
                            partial_pay: true,
                            points_round_to: 1,
                            points_rounding_rule: "UP",
                            program_display_name: response.body.loyalty_program.program_type,
                            program_name: config.shortName,
                            redeem_per_dollar: response.body.loyalty_program.redeem_per_dollar
                        }
                    }))
                    .catch(handleError)
            },
            getRegions (params = {}) {
                params.per_page = params.per_page || 100

                return Vue.http.get(buildPath('regions'), { params })
                    .then(response => response.body.items)
                    .catch(handleError)
            },
            getVenue (venueId) {
                return Vue.http.get(buildPath(`venues/${venueId}`))
                    .then(response => response.body)
                    .catch(handleError)
            },
            initSession (partnerId) {
                return Vue.http
                    .post(buildPath(`auth/sso/partner/${partnerId}`))
                    .then(response => response.body)
                    .catch(handleError)
            },
            placeOrder (order, config) {
                return Vue.http
                    .post(buildPath('orders'), order, config || {})
                    .then(response => response.body)
                    .catch(handleError)
            },
            search (query, params = {}, config = {}) {
                const searchParams = removeUnneededValues({
                    q: query,
                    date_end: params.date_end,
                    date_start: params.date_start,
                    page: params.page,
                    region_id: params.location_id
                })

                return Vue.http.get(buildPath('search'), { params: searchParams })
                    .then(mapSearch)
                    .catch(handleError)
            },
            searchLocations (query) {
                query = query.toLowerCase()

                return this.getRegions()
                    .then(regions => {
                        return regions.filter(region =>
                            region.city.toLowerCase().includes(query) || region.state.toLowerCase().includes(query)
                        ).map(mapLocation)
                    })
                    .catch(handleError)
            }
        }
    }
}
